import React, {useCallback, useMemo, useState} from 'react';
import {EyeIcon, EyeOffIcon} from '../components/icons';
import OtpDialog from '../components/OtpDialog';
import AuthShell from '../components/auth/AuthShell';
import SmartCaptcha, {isCaptchaEnabled} from '../components/auth/SmartCaptcha';
import {appName} from '../components/landing/content';
import authService from '../service/AuthService';
import {useDocumentTitle} from '../hooks/useDocumentTitle';

interface LoginFormData {
    username: string;
    password: string;
    rememberMe: boolean;
}

export default function Login() {
    useDocumentTitle();
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [otpRequired, setOtpRequired] = useState(false);
    const [captchaToken, setCaptchaToken] = useState('');
    const [formData, setFormData] = useState<LoginFormData>({
        username: '',
        password: '',
        rememberMe: false
    });

    /**
     * Куда вернуть после входа. По умолчанию — в гардероб (`/home`), а не на «/»: корень
     * отдаёт публичный лендинг, и вошедший пользователь после успешного логина видел
     * страницу «зарегистрируйтесь» вместо своих вещей (Q19).
     */
    const redirectUrl = useMemo(() => {
        const params = new URLSearchParams(window.location.search);
        const requested = params.get('redirectUrl');
        return requested && requested !== '/' ? requested : '/home';
    }, []);

    const captchaRequired = isCaptchaEnabled();
    const handleCaptchaToken = useCallback((token: string) => setCaptchaToken(token), []);

    const handleInputChange = (field: keyof LoginFormData, value: string | boolean) => {
        setFormData(prev => ({...prev, [field]: value}));
    };

    const onFinish = async (e: React.FormEvent) => {
        e.preventDefault();
        setLoading(true);
        setError('');

        try {
            const {mfaRequired} = await authService.passwordLogin(
                formData.username, formData.password, formData.rememberMe, captchaToken);
            if (mfaRequired) {
                // Пароль принят, но у аккаунта включена 2FA — вход завершает код.
                setOtpRequired(true);
                return;
            }
            window.location.href = redirectUrl;
        } catch {
            setError('Неверный логин или пароль');
        } finally {
            setLoading(false);
        }
    };

    const handleVerifyOtp = async (code: string) => {
        await authService.verifyOtp(code);
        window.location.href = redirectUrl;
    };

    const closeOtp = () => {
        // Незавершённый вход сбрасывается: придержанные токены больше не нужны.
        authService.cancelMfa();
        setOtpRequired(false);
    };

    return (
        <>
            <AuthShell
                heading={<>Добро пожаловать<br/>в <span className="text-brand-600 dark:text-brand-400">{appName()}</span></>}
                lead="Ваш умный помощник для идеального гардероба. Создавайте образы, получайте рекомендации и экономьте время каждый день."
            >
                <div data-testid="login-page">
                    <h2 className="mt-6 text-2xl font-bold text-center text-ink-900 dark:text-ink-50">
                        Вход в аккаунт
                    </h2>
                    <p className="mt-2 text-sm text-center text-ink-500 dark:text-ink-400">
                        Добро пожаловать! Введите ваши данные.
                    </p>

                    {error && (
                        <div
                            role="alert"
                            className="mt-6 p-3.5 rounded-xl bg-red-50 dark:bg-red-500/10 border border-red-200 dark:border-red-500/30"
                        >
                            <p className="text-sm text-red-700 dark:text-red-300">{error}</p>
                        </div>
                    )}

                    <form onSubmit={onFinish} className="mt-6 space-y-5">
                        <div>
                            <label htmlFor="username"
                                   className="block text-sm font-medium text-ink-700 dark:text-ink-200 mb-2">
                                Логин
                            </label>
                            <input
                                id="username"
                                type="text"
                                value={formData.username}
                                onChange={(e) => handleInputChange('username', e.target.value)}
                                className="input-field h-12"
                                placeholder="Введите логин"
                                autoComplete="username"
                                required
                                autoFocus
                            />
                        </div>

                        <div>
                            <label htmlFor="password"
                                   className="block text-sm font-medium text-ink-700 dark:text-ink-200 mb-2">
                                Пароль
                            </label>
                            <div className="relative">
                                <input
                                    id="password"
                                    type={showPassword ? 'text' : 'password'}
                                    value={formData.password}
                                    onChange={(e) => handleInputChange('password', e.target.value)}
                                    className="input-field h-12 pr-12"
                                    placeholder="Введите пароль"
                                    autoComplete="current-password"
                                    required
                                />
                                <button
                                    type="button"
                                    className="absolute inset-y-0 right-0 px-3 flex items-center text-ink-400 hover:text-ink-600 dark:hover:text-ink-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-500"
                                    aria-label={showPassword ? 'Скрыть пароль' : 'Показать пароль'}
                                    onClick={() => setShowPassword(!showPassword)}
                                >
                                    {showPassword ? (
                                        <EyeOffIcon className="w-5 h-5"/>
                                    ) : (
                                        <EyeIcon className="w-5 h-5"/>
                                    )}
                                </button>
                            </div>
                        </div>

                        <div className="flex items-center justify-between gap-3">
                            <div className="flex items-center">
                                <input
                                    id="rememberMe"
                                    type="checkbox"
                                    checked={formData.rememberMe}
                                    onChange={(e) => handleInputChange('rememberMe', e.target.checked)}
                                    className="h-4 w-4 rounded border-ink-300 dark:border-white/20 text-brand-600 focus:ring-brand-500"
                                />
                                <label htmlFor="rememberMe" className="ml-2 block text-sm text-ink-700 dark:text-ink-200">
                                    Запомнить меня
                                </label>
                            </div>
                            <a
                                href="/forgot-password"
                                className="text-sm font-medium text-brand-600 dark:text-brand-400 hover:underline"
                            >
                                Забыли пароль?
                            </a>
                        </div>

                        <SmartCaptcha onToken={handleCaptchaToken}/>

                        <button
                            type="submit"
                            data-testid="login-submit"
                            disabled={loading || (captchaRequired && !captchaToken)}
                            className="btn-accent w-full h-12 text-[15px]"
                        >
                            {loading ? (
                                <>
                                    <span className="animate-spin rounded-full h-5 w-5 border-2 border-ink-900/30 border-t-ink-900"/>
                                    <span>Вход...</span>
                                </>
                            ) : (
                                'Войти'
                            )}
                        </button>

                        <a href="/register" className="btn-secondary w-full h-12 text-[15px]">
                            Создать аккаунт
                        </a>
                    </form>

                    <p className="mt-6 text-xs leading-5 text-center text-ink-500 dark:text-ink-400">
                        Продолжая, вы соглашаетесь с условиями использования
                        и политикой конфиденциальности Clothes.
                    </p>
                </div>
            </AuthShell>

            {otpRequired && (
                <OtpDialog onVerify={handleVerifyOtp} onClose={closeOtp}/>
            )}
        </>
    );
}
